import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Phone, 
  Shield, 
  Check, 
  X, 
  Loader2,
  AlertCircle,
  Link as LinkIcon,
  Lock
} from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectTelegramPage() {
  const [status, setStatus] = useState({ connected: false, phone: null });
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('phone'); // phone, verify, 2fa
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [processing, setProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/telegram/status`, { withCredentials: true });
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load Telegram status');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/telegram/send-code`, 
        { phone },
        { withCredentials: true }
      );
      setPhoneCodeHash(response.data.phone_code_hash);
      setStep('verify');
      toast.success('Verification code sent to your Telegram app!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify = async (with2FA = false) => {
    if (!code && !with2FA) {
      toast.error('Please enter the verification code');
      return;
    }
    if (with2FA && !password) {
      toast.error('Please enter your 2FA password');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = { 
        phone, 
        code, 
        phone_code_hash: phoneCodeHash,
        ...(with2FA && { password })
      };
      
      await axios.post(`${API}/telegram/verify`, payload, { withCredentials: true });
      toast.success('Telegram connected successfully!');
      setDialogOpen(false);
      resetForm();
      fetchStatus();
    } catch (error) {
      if (error.response?.status === 428 && error.response?.data?.detail === '2FA_REQUIRED') {
        // 2FA is required
        setStep('2fa');
        toast.info('Two-factor authentication required. Please enter your Telegram cloud password.');
      } else {
        toast.error(error.response?.data?.detail || 'Verification failed. Please check the code and try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDisconnect = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/telegram/disconnect`, {}, { withCredentials: true });
      toast.success('Telegram disconnected');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setStep('phone');
    setPhone('');
    setCode('');
    setPassword('');
    setPhoneCodeHash('');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="telegram-page-title">
            Connect Telegram
          </h1>
          <p className="text-muted-foreground mt-1">
            Link your Telegram account to start monitoring channels
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Alert className="border-primary/20 bg-primary/5">
            <AlertCircle className="w-4 h-4 text-primary" />
            <AlertDescription className="text-sm">
              <strong>Secure Connection:</strong> Your Telegram credentials are encrypted and stored securely. 
              We only access channels you explicitly choose to monitor.
            </AlertDescription>
          </Alert>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FaTelegram className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl">Telegram Account</CardTitle>
                  <CardDescription>
                    {status.connected 
                      ? `Connected as ${status.phone}` 
                      : 'Connect your account to monitor channels'}
                  </CardDescription>
                </div>
                <Badge 
                  variant={status.connected ? 'default' : 'secondary'}
                  className={status.connected ? 'bg-success text-success-foreground' : ''}
                >
                  {status.connected ? (
                    <><Check className="w-3 h-3 mr-1" /> Connected</>
                  ) : (
                    <><X className="w-3 h-3 mr-1" /> Not Connected</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {status.connected ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Phone Number</p>
                        <p className="text-sm text-muted-foreground">{status.phone}</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="destructive" 
                    onClick={handleDisconnect}
                    disabled={processing}
                    className="rounded-full"
                    data-testid="disconnect-telegram"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Disconnect Account
                  </Button>
                </div>
              ) : (
                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full gap-2" data-testid="connect-telegram-btn">
                      <LinkIcon className="w-4 h-4" />
                      Connect Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FaTelegram className="w-5 h-5 text-primary" />
                        {step === 'phone' && 'Enter Phone Number'}
                        {step === 'verify' && 'Verify Code'}
                        {step === '2fa' && 'Two-Factor Authentication'}
                      </DialogTitle>
                      <DialogDescription>
                        {step === 'phone' && 'Enter your Telegram phone number to receive a verification code'}
                        {step === 'verify' && 'Enter the verification code sent to your Telegram app'}
                        {step === '2fa' && 'Enter your Telegram cloud password (2FA)'}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {step === 'phone' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="+1234567890"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            data-testid="phone-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Include country code (e.g., +1 for US)
                          </p>
                        </div>
                        <Button 
                          onClick={handleSendCode}
                          disabled={processing || !phone}
                          className="w-full rounded-full"
                          data-testid="send-code-btn"
                        >
                          {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Send Verification Code
                        </Button>
                      </div>
                    )}
                    
                    {step === 'verify' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="code">Verification Code</Label>
                          <Input
                            id="code"
                            type="text"
                            placeholder="Enter code from Telegram"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            maxLength={6}
                            data-testid="code-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Check your Telegram app for the verification code
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => setStep('phone')}
                            className="flex-1 rounded-full"
                          >
                            Back
                          </Button>
                          <Button 
                            onClick={() => handleVerify(false)}
                            disabled={processing || !code}
                            className="flex-1 rounded-full"
                            data-testid="verify-btn"
                          >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Verify
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {step === '2fa' && (
                      <div className="space-y-4">
                        <Alert className="border-yellow-500/20 bg-yellow-500/5">
                          <Lock className="w-4 h-4 text-yellow-500" />
                          <AlertDescription className="text-sm">
                            Your Telegram account has Two-Factor Authentication enabled. 
                            Please enter your cloud password to continue.
                          </AlertDescription>
                        </Alert>
                        <div className="space-y-2">
                          <Label htmlFor="password">Cloud Password (2FA)</Label>
                          <Input
                            id="password"
                            type="password"
                            placeholder="Enter your 2FA password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="2fa-password-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            This is the password you set in Telegram Settings → Privacy & Security → Two-Step Verification
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => setStep('verify')}
                            className="flex-1 rounded-full"
                          >
                            Back
                          </Button>
                          <Button 
                            onClick={() => handleVerify(true)}
                            disabled={processing || !password}
                            className="flex-1 rounded-full"
                            data-testid="verify-2fa-btn"
                          >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Verify
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-success" />
                Security Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-success mt-0.5" />
                  <p>Your Telegram password is never stored - only a secure session token</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-success mt-0.5" />
                  <p>Sessions are encrypted and can be revoked at any time</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-success mt-0.5" />
                  <p>We only read messages from channels you explicitly monitor</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-success mt-0.5" />
                  <p>ToS-compliant authentication using official Telegram API</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
